import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { usePaymentOptions } from '@/hooks/usePaymentOptions';
import { useDonation } from '@/hooks/useDonation';
import { donorFormSchema, type DonorFormData } from '@/lib/utils/validation';
import AmountSelector from './AmountSelector';
import DonorForm from './DonorForm';
import RecurringToggle from './RecurringToggle';
import PaymentHandler from './PaymentHandler';
import { Button, Card, LoadingSpinner, Alert } from '@/components/ui';
import type { RecurringSchedule, Currency, PaymentGateway } from '@/types/donation';

interface DonationWidgetProps {
  preselectedAmount?: number; // In smallest unit (cents)
  preselectedSchedule?: RecurringSchedule;
}

function DonationWidget({ preselectedAmount = 0, preselectedSchedule = 'one_time' }: DonationWidgetProps = {}) {
  const navigate = useNavigate();
  const [amount, setAmount] = useState<number>(preselectedAmount); // In smallest unit
  const [recurringSchedule, setRecurringSchedule] = useState<RecurringSchedule>(preselectedSchedule);
  const [selectedGateway, setSelectedGateway] = useState<PaymentGateway | null>(null);

  // Update amount and schedule when props change
  useEffect(() => {
    if (preselectedAmount) setAmount(preselectedAmount);
    if (preselectedSchedule) setRecurringSchedule(preselectedSchedule);
  }, [preselectedAmount, preselectedSchedule]);

  // Fetch payment options based on detected region
  const { data: paymentOptions, isLoading: isLoadingOptions, error: optionsError } = usePaymentOptions();

  // Form handling
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<DonorFormData>({
    resolver: zodResolver(donorFormSchema),
    defaultValues: {
      recurringSchedule: preselectedSchedule,
    },
  });

  // Update form when state changes
  useEffect(() => {
    setValue('amount', amount);
  }, [amount, setValue]);

  useEffect(() => {
    setValue('recurringSchedule', recurringSchedule);
  }, [recurringSchedule, setValue]);

  // Donation mutation
  const {
    mutate: createDonation,
    data: donationResponse,
    isPending: isCreatingDonation,
    error: donationError,
    reset: resetDonation,
  } = useDonation();

  // Set default gateway if not set
  useEffect(() => {
    const gateways = paymentOptions?.data?.paymentOptions || [];
    if (gateways.length > 0 && !selectedGateway) {
      setSelectedGateway(gateways[0]?.gateway || null);
    }
  }, [paymentOptions, selectedGateway]);

  // Handle form submission
  const onSubmit = (data: DonorFormData) => {
    if (amount === 0) {
      return;
    }

    const optionsData = paymentOptions?.data;
    if (!optionsData) {
      return;
    }

    // Check minimum amount
    if (amount < optionsData.minimumAmount) {
      return;
    }

    createDonation({
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      phone: data.phone,
      amount,
      currency: optionsData.currency.default as Currency,
      recurringSchedule,
      region: optionsData.region.code,
      preferredGateway: selectedGateway || undefined,
      callbackUrl: `${window.location.origin}/callback`,
    });
  };

  // Loading state
  if (isLoadingOptions) {
    return (
      <Card className="max-w-2xl mx-auto">
        <LoadingSpinner message="Loading payment options..." />
      </Card>
    );
  }

  // Error state
  if (optionsError || !paymentOptions || !paymentOptions.data) {
    return (
      <Card className="max-w-2xl mx-auto">
        <Alert variant="error" title="Unable to Load Payment Options">
          {optionsError?.message || 'Failed to load payment options. Please try again later.'}
        </Alert>
      </Card>
    );
  }

  const { paymentOptions: availableGateways = [], currency, minimumAmount } = paymentOptions.data;

  const currencySymbol = currency.symbol;

  // Validation errors
  const amountError =
    amount > 0 && amount < minimumAmount
      ? `Minimum donation is ${currencySymbol}${(minimumAmount / 100).toLocaleString()}`
      : undefined;

  const hasAmountError = amount === 0 || !!amountError;

  const getScheduleTitle = () => {
    if (recurringSchedule === 'monthly') return 'Monthly';
    if (recurringSchedule === 'yearly') return 'Yearly';
    return 'One Time';
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Hero Section */}
      <div className="text-center mb-8">
        <h1 className="text-3xl md:text-4xl font-bold mb-3">
          <span className="text-gold">{preselectedAmount ? getScheduleTitle() : 'Support'}</span>{' '}
          <span className="text-white">{preselectedAmount ? 'Covenant' : 'the Movement'}</span>
        </h1>
        <p className="text-gray-400 text-base max-w-2xl mx-auto">
          {preselectedAmount
            ? 'Complete your information below to join the covenant'
            : 'Your contribution helps us create world-class content and provide quality entertainment for families everywhere.'}
        </p>
      </div>

      <Card className="backdrop-blur-sm bg-dark-card/90">
        {/* Show error if donation creation fails */}
        {donationError && (
          <Alert variant="error" title="Donation Failed" className="mb-6">
            {donationError.message}
          </Alert>
        )}

        <form
          onSubmit={handleSubmit(onSubmit, (err) => {
            console.error('Donation form validation failed:', err);
          })}
          className="space-y-8"
        >
          {/* Amount Selection - Only show if no preselected amount or if it's PWYL (0) */}
          {(!preselectedAmount || preselectedAmount === 0) && (
            <AmountSelector
              value={amount}
              onChange={setAmount}
              currency={currency.default as Currency}
              currencySymbol={currencySymbol}
              minimumAmount={minimumAmount}
              error={amountError}
            />
          )}

          {/* Show selected amount if preselected and not PWYL */}
          {preselectedAmount > 0 && (
            <div className="text-center py-5 bg-dark-lighter border border-gold/30 rounded-lg">
              <p className="text-gray-400 text-xs uppercase tracking-wider mb-2">Your Contribution</p>
              <p className="text-3xl font-bold text-gold">
                {currencySymbol}{(amount / 100).toLocaleString()}
              </p>
              <p className="text-gray-400 text-xs mt-2">
                {recurringSchedule === 'monthly' ? 'per month' : recurringSchedule === 'yearly' ? 'per year' : 'one time'}
              </p>
            </div>
          )}

          {/* Recurring Toggle - Only show if no preselected schedule */}
          {!preselectedSchedule && import.meta.env.VITE_ENABLE_RECURRING_DONATIONS === 'true' && (
            <RecurringToggle
              value={recurringSchedule}
              onChange={setRecurringSchedule}
              amount={amount}
              currencySymbol={currencySymbol}
            />
          )}

          {/* Donor Information */}
          <DonorForm register={register} errors={errors} />

          {/* Available Payment Gateways Selection */}
          {availableGateways.length > 0 && (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-300 uppercase tracking-wider">
                Select Payment Method
                <span className="text-gold ml-1">*</span>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {availableGateways.map((gw) => (
                  <button
                    key={gw.gateway}
                    type="button"
                    onClick={() => setSelectedGateway(gw.gateway)}
                    className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all duration-200 ${selectedGateway === gw.gateway
                      ? 'border-gold bg-gold/10'
                      : 'border-dark-border bg-dark-card hover:border-gold/30'
                      }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${selectedGateway === gw.gateway ? 'bg-gold' : 'bg-transparent'}`} />
                      <span className={`font-semibold ${selectedGateway === gw.gateway ? 'text-white' : 'text-gray-400'}`}>
                        {gw.name}
                      </span>
                    </div>
                    {gw.logo && (
                      <img src={gw.logo} alt={gw.name} className="h-6 w-auto grayscale brightness-200" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-full bg-gold hover:bg-gold-light text-dark font-bold text-base"
            isLoading={isCreatingDonation}
            disabled={hasAmountError || isCreatingDonation}
          >
            {isCreatingDonation
              ? 'Processing...'
              : `Donate ${amount > 0 ? `${currencySymbol}${(amount / 100).toLocaleString()}` : 'Now'}`}
          </Button>

          {/* Security Notice */}
          <p className="text-center text-sm text-gray-500">
            🔒 Secure payment powered by {availableGateways[0]?.name}. Your payment information is
            encrypted and never stored on our servers.
          </p>
        </form>
      </Card>

      {/* Client-side Payment Initiation */}
      {donationResponse && (
        <PaymentHandler
          response={donationResponse}
          gateway={selectedGateway as PaymentGateway}
          onSuccess={(data) => {
            console.log('Payment successful:', data);
            navigate(`/success?id=${donationResponse.data.donation.id}`);
          }}
          onCancel={() => {
            resetDonation();
          }}
          onError={(error) => {
            console.error('Payment error:', error);
          }}
        />
      )}
    </div>
  );
}

export default DonationWidget;
